import React, { useState, useEffect } from 'react';
import { sendInquiry, applyForJob } from '../utils/apiService';

const InquiryModal = ({ isOpen, onClose, initialService, mode = 'inquiry', jobId }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        service_name: '',
        resume: null
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const isApplyMode = mode === 'apply';

    useEffect(() => {
        if (initialService) {
            setFormData(prev => ({ ...prev, service_name: initialService }));
        }
    }, [initialService]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'resume') {
            setFormData(prev => ({ ...prev, resume: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        // Clear field error when user types
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus({ type: '', message: '' });
        setFieldErrors({});

        let result;
        if (isApplyMode) {
            const dataToSubmit = new FormData();
            dataToSubmit.append('job_id', jobId);
            dataToSubmit.append('name', formData.name);
            dataToSubmit.append('email', formData.email);
            dataToSubmit.append('phone', formData.phone);
            dataToSubmit.append('message', formData.message);
            if (formData.resume) {
                dataToSubmit.append('resume', formData.resume);
            }
            result = await applyForJob(dataToSubmit);
        } else {
            const dataToSubmit = new FormData();
            dataToSubmit.append('name', formData.name);
            dataToSubmit.append('email', formData.email);
            dataToSubmit.append('phone', formData.phone);
            dataToSubmit.append('message', formData.message);
            dataToSubmit.append('service_name', formData.service_name);
            result = await sendInquiry(dataToSubmit);
        }

        if (result && result.status) {
            setStatus({ type: 'msg-success', message: result.message || (isApplyMode ? 'Thank you for your application!' : 'Thank you for your inquiry!') });
            // Clear form
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: '',
                service_name: initialService || '',
                resume: null
            });

            // Close after 2 seconds
            setTimeout(() => {
                setStatus({ type: '', message: '' });
                onClose();
            }, 2000);
        } else {
            if (result.errors) {
                setFieldErrors(result.errors);
                setStatus({ type: 'msg-error', message: result.message || 'Please fix the errors below.' });
            } else {
                setStatus({ type: 'msg-error', message: result.message || 'Something went wrong. Please try again.' });
            }
        }
        setLoading(false);
    };

    if (!isOpen) return null;

    const renderFieldError = (fieldName) => {
        if (fieldErrors[fieldName]) {
            return (
                <div className="error-text text-danger mt-1" style={{ fontSize: '13px' }}>
                    {fieldErrors[fieldName].join(' ')}
                </div>
            );
        }
        return null;
    };

    return (
        <div id="inquiryModal" className={`modal fade ${isOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-4 bg-dark-2">
                    {/* Modal Header */}
                    <div className="modal-header">
                        <h5 className="modal-title">{isApplyMode ? 'Apply Now' : 'Inquiry'}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body">
                        {status.message && !Object.keys(fieldErrors).length && (
                            <div className={`flat-alert mb-20 ${status.type}`}>
                                {status.message}
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="form-contact-us inquery-form sidebar-form">
                            <div className="mb-20">
                                <input
                                    type="text"
                                    name="name"
                                    className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                                    placeholder="Enter your name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                                {renderFieldError('name')}
                            </div>

                            <div className="mb-20">
                                <input
                                    type="email"
                                    name="email"
                                    className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                                    placeholder="Enter email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                                {renderFieldError('email')}
                            </div>

                            <div className="mb-20">
                                <input
                                    type="tel"
                                    name="phone"
                                    className={`form-control ${fieldErrors.phone ? 'is-invalid' : ''}`}
                                    placeholder="Enter phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                                {renderFieldError('phone')}
                            </div>

                            {isApplyMode && (
                                <div className="mb-20">
                                    <label className="mb-2 text-white d-block">Upload Resume (PDF/Doc)</label>
                                    <input
                                        type="file"
                                        name="resume"
                                        className={`form-control ${fieldErrors.resume ? 'is-invalid' : ''}`}
                                        onChange={handleChange}
                                        accept=".pdf,.doc,.docx"
                                        required
                                    />
                                    {renderFieldError('resume')}
                                </div>
                            )}

                            <div className="mb-20">
                                <textarea
                                    name="message"
                                    className={`form-control ${fieldErrors.message ? 'is-invalid' : ''}`}
                                    rows="3"
                                    placeholder={isApplyMode ? "Your message or cover letter" : "Your inquiry"}
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                ></textarea>
                                {renderFieldError('message')}
                            </div>

                            <button type="submit" className="tf-btn w-full justify-content-center" disabled={loading}>
                                <span>{loading ? 'Sending...' : (isApplyMode ? 'Submit Application' : 'Submit Inquiry')}</span>
                                {!loading && <i className="icon-arrow-right"></i>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InquiryModal;
